import React, { memo, useMemo } from 'react';

import { getLanguage, SyntaxHighlighting } from '@atlassian/bitkit-code';

import {
  ChunkEntry,
  DiffInlineRenderProp,
  Line,
  LineAnnotation,
  OnAddComment,
} from '../../common/types';
import { getCurrentLineAnnotations } from '../../common/util/get-line-annotations';
import groupChunk from '../../common/util/group-chunk';
import { lineHasInlineContent } from '../../common/util/line-has-inline-content';

import ChunkControls from './chunk-controls';
import { CodeLine } from './code-line';
import { SideBySideDiffLines, UnifiedDiffLines } from './diff-lines';

type OwnProps = {
  activePermalink?: string;
  chunk: ChunkEntry;
  filePath?: string;
  inlineContent?: DiffInlineRenderProp;
  onAddComment?: OnAddComment;
  onShowMoreLines: (shouldExpandDown: boolean) => void;
  showPermalinks: boolean;
  onPermalinkClick?: (permalink: string) => void;
  fileAnnotations?: LineAnnotation[];
  isPrAnnotationsEnabled?: boolean;
  isSideBySide?: boolean;
  isFirstChunk: boolean;
  isLastChunk: boolean;
};

export const Chunk = memo(
  ({
    chunk,
    onShowMoreLines,
    activePermalink,
    filePath,
    showPermalinks,
    onPermalinkClick,
    onAddComment,
    inlineContent,
    fileAnnotations,
    isPrAnnotationsEnabled,
    isSideBySide,
    isFirstChunk,
    isLastChunk,
  }: OwnProps) => {
    const { before, after } = useMemo(() => groupChunk(chunk), [chunk]);
    let content;
    if (!isSideBySide) {
      content = chunk.changes.map((line, index) => (
        <div
          className="lines-wrapper"
          key={`line-${line.oldLine}-${line.newLine}-${index}`}
        >
          <CodeLine
            activePermalink={activePermalink}
            filePath={filePath}
            index={index}
            line={line}
            showPermalinks={showPermalinks}
            onPermalinkClick={onPermalinkClick}
            onAddComment={onAddComment}
            lineAnnotations={getCurrentLineAnnotations(
              fileAnnotations,
              line.newLine
            )}
            isPrAnnotationsEnabled={isPrAnnotationsEnabled}
            inlineContent={
              lineHasInlineContent(line, inlineContent)
                ? inlineContent
                : undefined
            }
          />
        </div>
      ));
    } else {
      content = before.changes.map((_, index) => {
        const beforeLine = before.changes[index];
        const afterLine = after.changes[index];

        // unchanged lines' content should only appear on left (before) side
        const isUnchanged =
          beforeLine.type === 'normal' && afterLine.type === beforeLine.type;

        return (
          <div
            className="lines-wrapper"
            key={`line-${beforeLine.oldLine}-${afterLine.newLine}-${index}`}
          >
            <CodeLine
              activePermalink={activePermalink}
              filePath={filePath}
              index={index}
              line={beforeLine}
              showPermalinks={showPermalinks}
              hideNewLines
              onPermalinkClick={onPermalinkClick}
              onAddComment={onAddComment}
              inlineContent={
                lineHasInlineContent(beforeLine, inlineContent)
                  ? inlineContent
                  : undefined
              }
            />
            <CodeLine
              activePermalink={activePermalink}
              filePath={filePath}
              index={index}
              line={afterLine}
              showPermalinks={showPermalinks}
              hideOldLines
              onPermalinkClick={onPermalinkClick}
              onAddComment={onAddComment}
              lineAnnotations={getCurrentLineAnnotations(
                fileAnnotations,
                afterLine.newLine
              )}
              isPrAnnotationsEnabled={isPrAnnotationsEnabled}
              inlineContent={
                !isUnchanged && lineHasInlineContent(afterLine, inlineContent)
                  ? inlineContent
                  : undefined
              }
            />
          </div>
        );
      });
    }

    return (
      <ChunkControls
        before={chunk.extra.before}
        heading={chunk.content}
        isLoadingBelow={chunk.extra.isLoadingBelow}
        isLoadingAbove={chunk.extra.isLoadingAbove}
        onShowMoreLines={onShowMoreLines}
        isSideBySide={isSideBySide}
        isDummyChunk={chunk.isDummyChunk}
        isFirstChunk={isFirstChunk}
        isLastChunk={isLastChunk}
      >
        {content}
      </ChunkControls>
    );
  }
);

export const SyntaxHighlightedChunk = memo(
  ({ chunk, filePath, isSideBySide, ...rest }: OwnProps) => {
    const { before, after } = useMemo(() => groupChunk(chunk), [chunk]);
    const afterAsString = useMemo(
      () => linesToString(after.changes),
      [after.changes]
    );
    const beforeAsString = useMemo(
      () => linesToString(before.changes),
      [before.changes]
    );
    const language = getLanguage(filePath ?? '');

    return (
      <SyntaxHighlighting
        source={[beforeAsString, afterAsString]}
        language={language?.prism}
      >
        {([beforeHighlighted, afterHighlighted]) => {
          const DiffLinesComponent = isSideBySide
            ? SideBySideDiffLines
            : UnifiedDiffLines;
          return (
            <DiffLinesComponent
              chunk={chunk}
              filePath={filePath}
              before={before}
              after={after}
              beforeHighlighted={beforeHighlighted}
              afterHighlighted={afterHighlighted}
              {...rest}
            />
          );
        }}
      </SyntaxHighlighting>
    );

    function linesToString(changes: Line[]): string {
      return (
        changes
          // We receive a list of changes that's designed to allow unchanged
          // lines to have the same index in both the before and after lists by
          // filling in gaps with empty lines. We want the original source, so
          // filter those empty lines out.
          .filter(l => l.type !== 'empty')

          // Now we strip the +/- column from the start of the diff lines
          .map(c => {
            if (c.content.substring) {
              return c.content?.substring(1);
            }
            return '';
          })

          // And finally join them together into a single string
          .join('\n')
      );
    }
  }
);
